
import ebayCategoryLookup from '../../../data/categories/ebay-categories.json';
import ebayCategories from '../../../data/categories/ebay-categories-two.json';
import etsyCategoryLookup from '../../../data/categories/etsy-categories.json';
import etsyCategories from '../../../data/categories/etsy-categories-two.json';

import facebookCategoryLookup from '../../../data/categories/facebook-categories.json'

import mercariCategoryLookup from '../../../data/categories/mercari-categories.json'
import mercariCategories from '../../../data/categories/mercari-categories-two.json'

import poshmarkCategoryLookup from '../../../data/categories/poshmark-categories.json'
import poshmarkCategories from '../../../data/categories/poshmark-categories-two.json'

import categoriesMapped from '../../../data/categories/categories-mapped.json'

const CategoryHelpers = {

  categoriesForMerchant: (merchantName = '') => {
    switch (merchantName) {
        case 'ebay':
          return ebayCategories
        case 'etsy':
          return etsyCategories
        case 'facebook':
          return facebookCategoryLookup // There is only one level so the lookup table works
        case 'mercari':
          return mercariCategories
        case 'tradesy':
          // TODO
          return null
        case 'poshmark':
        case '': // default
          return poshmarkCategories
        default:
          console.error('Unhandled merchant name given: ', merchantName)
          return null
    }
  },

  isCategoryPathCompleteForMerchant: (initialCategoryPath, merchantName) => {
    const startTime = (new Date()).getTime()
    let categoriesNode = CategoryHelpers.categoriesForMerchant(merchantName)
    let categoryPath = initialCategoryPath.slice(0)

    if (!categoryPath.length) {
      return false
    }
    let currentCategoryKey = categoryPath[0]

    while (categoryPath.length > 0) {

      // Be conservative about the incompleteness assertion
      // If we can't find the category key, just return complete/true
      if (!categoriesNode[currentCategoryKey]) {
        return true
      }
      if (
        categoriesNode[currentCategoryKey] && 
        categoriesNode[currentCategoryKey].children
      ) {
        categoriesNode = categoriesNode[currentCategoryKey].children
      } else {
        categoriesNode = null
      }
      categoryPath = categoryPath.slice(1) // array with first item chopped off
      currentCategoryKey = categoryPath[0]
    }

    if (categoriesNode) {
      return false
    }

    return true
  },

  lookupTableForMerchant: (merchantName = '') => {
    switch (merchantName) {
        case 'ebay':
          return ebayCategoryLookup
        case 'etsy':
          return etsyCategoryLookup
        case 'facebook':
          return facebookCategoryLookup
        case 'mercari':
          return mercariCategoryLookup
        case 'tradesy':
          // TODO
          return null
        case 'poshmark':
        case '': // default
          return poshmarkCategoryLookup
        default:
          console.error('Unhandled merchant name given: ', merchantName)
          return null
    }
  },

  poshmarkCategoryPathForMerchantKey: (categoryKey, merchantName) => {
    for (let [poshmarkCategoryKey, merchantKeys] of Object.entries(categoriesMapped)) {
      if (merchantKeys[merchantName] === categoryKey) {
        const poshmarkCategoryValues = poshmarkCategoryLookup[poshmarkCategoryKey]
        if (!poshmarkCategoryValues || !poshmarkCategoryValues.categoryPath) {
          return []
        }
        return poshmarkCategoryValues.categoryPath
      }
    }
    return []
  },

  mappedCategoryPath: (poshmarkCategoryPath, merchantName) => {
    if (!poshmarkCategoryPath || !poshmarkCategoryPath.length || !merchantName) {
      return []
    }
    const poshmarkCategoryKey = poshmarkCategoryPath[poshmarkCategoryPath.length - 1]
    const categoriesLookup = CategoryHelpers.lookupTableForMerchant(merchantName)

    if (!categoriesMapped[poshmarkCategoryKey] || !categoriesMapped[poshmarkCategoryKey][merchantName]) {
      console.log('No mapping found for key: ', poshmarkCategoryKey, merchantName)
      return []
    }

    const merchantCategoryKey = categoriesMapped[poshmarkCategoryKey][merchantName]
    if (categoriesLookup && categoriesLookup[merchantCategoryKey] && categoriesLookup[merchantCategoryKey].categoryPath) {
       return categoriesLookup[merchantCategoryKey].categoryPath
    }

    // Load heavier lookup table
    const categories = CategoryHelpers.categoriesForMerchant(merchantName)
    if (categories && categories[merchantCategoryKey] && categories[merchantCategoryKey].categoryPath) {
      return categories[merchantCategoryKey].categoryPath
    }

    return []
  },

  autoFilledCategorySpecificsForMerchant: (merchantName, item, categorySpecificsData) => {

    const normalizedSize = CategoryHelpers.normalizedSizeForItem(item)
    switch (merchantName) {
      case 'mercari':
        return CategoryHelpers.mercariCategorySpecificsForNormalizedSize(normalizedSize, categorySpecificsData)
      case 'ebay':
        return CategoryHelpers.ebayCategorySpecificsForNormalizedSize(normalizedSize, categorySpecificsData)
      case 'etsy':
        return CategoryHelpers.etsyCategorySpecificsForNormalizedSize(normalizedSize, categorySpecificsData)
      case 'poshmark':
        return CategoryHelpers.poshmarkCategorySpecificsForNormalizedSize(normalizedSize, categorySpecificsData)
      default:
        return {}
    }
  },

  _scaleKeyForCategorySpecificsSize: (size, categorySpecificsData, sizeKey = null) => {
    if (
      !size || 
      !categorySpecificsData || 
      !categorySpecificsData[sizeKey] ||
      !categorySpecificsData[sizeKey].scales
    ) {
      return null
    }
    const scales = categorySpecificsData[sizeKey].scales
    if (!size.scale) {
      // standard / regular scale
      return Object.keys(scales).find(key => {
        const k = key.toLowerCase()
        return (
          k === 'na_scale' ||
          k === 'standard' ||
          k === 'regular'
        )
      }) 
    } else {
      // If we find the normalized scale name in the list of possible scales, use that
      // else try to fallback to the scale name
      return Object.keys(scales).find(key => {
        const k = key.toLowerCase()
        return k.indexOf(size.scale) > -1
      }) || size.scale
    }
  },

  _optionsForCategorySpecificsSize: (size, categorySpecificsData, sizeKey = null) => {
    if (
      !size || 
      !categorySpecificsData || 
      !categorySpecificsData[sizeKey] ||
      !categorySpecificsData[sizeKey].scales
    ) {
      return []
    }

    const scales = categorySpecificsData[sizeKey].scales
    const scaleKey = CategoryHelpers._scaleKeyForCategorySpecificsSize(size, categorySpecificsData, sizeKey)
    if (
      scales[scaleKey] &&
      scales[scaleKey].options
    ) {
      return Object.values(scales[scaleKey].options || {})
    }
    return []
  },

  _optionMatchForCategorySpecificsSize: (size, categorySpecificsData, sizeKey = null) => {
    const options = CategoryHelpers._optionsForCategorySpecificsSize(size, categorySpecificsData, sizeKey)
    const match = CategoryHelpers._sizeMatchForOptions(size, options)
    if (match) {
      return match
    }
    return null
  },

  _optionsForSizeType: (sizeType = {}) => {
    if (
      sizeType &&
      sizeType.scales &&
      sizeType.scales.na_scale &&
      sizeType.scales.na_scale.options
    ) {
      return Object.values(sizeType.scales.na_scale.options || {})
    }
    return []
  },

  _sizeMatchForOptions: (size, options = []) => {
    if (!size) {
      return null
    }

    let match = options.find(option => option.display === size.search || option.display === size.display)
    if (!match) {
      match = options.find(option => option.display.indexOf(size.search) > -1)
    }

    const assignMatchForMapping = (a, b) => {
      if (!match && size.display === a) {
        match = options.find(option => option.display.indexOf(b) > -1)
      }
      if (!match && size.display === b) {
        match = options.find(option => option.display.indexOf(a) > -1)
      }
    }

    assignMatchForMapping('XXL', '2XL')

    return match
  },

  ebayCategorySpecificsForNormalizedSize: (size, categorySpecificsData = {}) => {
    if (!size) {
      return {}
    }
    const match = CategoryHelpers._optionMatchForCategorySpecificsSize(size, categorySpecificsData, 'Size')
    let data = {}
    if (match) {
      data['Size'] = {
        display: match.display,
        id: match.id,
      }
    } else {
      const shoeSizeKey = Object.keys(categorySpecificsData).find(k => k.indexOf('US Shoe Size') > -1)
      if (shoeSizeKey) {
        const shoeSizeOptions = CategoryHelpers._optionsForSizeType(categorySpecificsData[shoeSizeKey])
        const shoeSizeMatch = CategoryHelpers._sizeMatchForOptions(size, shoeSizeOptions)
        if (shoeSizeMatch) {
          data[shoeSizeKey] = {
            display: shoeSizeMatch.display,
            id: shoeSizeMatch.id,
          }
        }
      }

      const bottomsSizeKey = Object.keys(categorySpecificsData).find(k => k.indexOf('Bottoms Size') > -1)
      if (bottomsSizeKey) {
        const bottomsSizeOptions = CategoryHelpers._optionsForSizeType(categorySpecificsData[bottomsSizeKey])
        const bottomsSizeMatch = CategoryHelpers._sizeMatchForOptions(size, bottomsSizeOptions)
        if (bottomsSizeMatch) {
          data[bottomsSizeKey] = {
            display: bottomsSizeMatch.display,
            id: bottomsSizeMatch.id,
          }
        }
      }
    }

    const sizeType = categorySpecificsData['Size Type']
    const sizeTypeOptions = CategoryHelpers._optionsForSizeType(sizeType)
    const regularOption = sizeTypeOptions.find(o => o.id === 'Regular')
    const sizeTypeMatch = CategoryHelpers._sizeMatchForOptions(size, sizeTypeOptions)
    if (sizeTypeMatch) {
      // If we found a size type match, use it. Otherwise default to regular if it exists     
      data['Size Type'] = {
        display: sizeTypeMatch.display,
        id: sizeTypeMatch.id,
      }
    } else if (regularOption) {
      data['Size Type'] = {
        display: regularOption.display,
        id: regularOption.id,
      }
    }

    return data
  },

  etsyCategorySpecificsForNormalizedSize: (size, categorySpecificsData = {}) => {
    if (!size) {
      return {}
    }
    const sizeValues = Object.values(categorySpecificsData).find(o => o.display === 'Size') || {}
    const sizeKey = +sizeValues.id
    if (!sizeKey) {
      return {}
    }
    const match = CategoryHelpers._optionMatchForCategorySpecificsSize(size, categorySpecificsData, sizeKey)
    if (match) {
      return {
        [sizeKey]: {
          display: match.display,
          id: match.id,
        }
      }
    }
    return {}
  },

  mercariCategorySpecificsForNormalizedSize: (size, categorySpecificsData) => {
    if (!size) {
      return {}
    }
    const match = CategoryHelpers._optionMatchForCategorySpecificsSize(size, categorySpecificsData, 'sizeId')
    if (match) {
      return {
        sizeId: {
          display: match.display,
          id: match.id,
        }
      }
    }

    const options = CategoryHelpers._optionsForCategorySpecificsSize(size, categorySpecificsData, 'sizeId')
    if (!options.length) {
      return {}
    }

    // Shoe sizes
    if (size.shoe_size_us && size.shoe_size_euro) {
      const mercariShoeSizeDisplay = `${size.shoe_size_us} (${size.shoe_size_euro})`
      let option = (
        options.find(option => option.display === mercariShoeSizeDisplay) ||
        options.find(option => option.display.indexOf(String(size.shoe_size_us)) === 0)
      )
      if (option) {
        return { sizeId: option }
      }
    }

    if (size.display === undefined || isNaN(+size.display)) {
      return {}
    }

    // Handle dress sizes
    // Format: `{?Size_Set} {Size_Name} ({Min_Size}-{Max_Size})`
    const mercariScaleNameForNormalizedScale = (scaleName) => {
      if (!scaleName || !scaleName.length) {
        return ''
      }
      // Mapping is conveniently just the uppercase of the normalized scale
      return `${scaleName[0].toUpperCase()}${scaleName.substr(1)}`
    }

    const sizeNumeric = +size.display
    const mercariScaleName = mercariScaleNameForNormalizedScale(size.scale)

    for (let option of options) {

      if (!option.display || option.display.indexOf(mercariScaleName) === -1) {
        continue
      }

      const match = / \([\d-]+\)/.exec(option.display)
      if (!match || match.length < 2) {
        continue
      }

      if (match[1].indexOf('-') > -1) {
        const [ minSizeName, maxSizeName ] = match[1].split('-')
        const [ minSizeNumeric, maxSizeNumeric ] = [ +minSizeName, +maxSizeName ]
        if (
          sizeNumeric >= minSizeNumeric &&
          sizeNumeric <= maxSizeNumeric
        ) {
          return { sizeId: option }
        }
      } else {
        const mercariSizeNumeric = +match[1]
        if (sizeNumeric === mercariSizeNumeric) {
          return { sizeId: option }
        }
      }
    }

    return {}
  },

  poshmarkCategorySpecificsForNormalizedSize: (size, categorySpecificsData) => {
    if (!size) {
      return {}
    }

    // If we have no normalized size data, try to autofill obvious ones (e.g. One Size)
    if (
      !categorySpecificsData ||
      !categorySpecificsData.size ||
      !categorySpecificsData.size.scales
    ) {
      return {}
    }

    const scaleValues = Object.values(categorySpecificsData.size.scales)

    if (
      scaleValues.length === 1 &&
      scaleValues[0].options ||
      Object.values(scaleValues[0].options).length === 1
    ) {
      // There is exactly one option/scale
      const scale = scaleValues[0]
      if (!scale.id) {
        return {}
      }
      const option = Object.values(scaleValues[0].options)[0]
      // { size: { size_obj: { display: string, id: string... }, size_set_tags: [ "standard" ]}}
      return {
        size: {
          size_obj: option,
          size_set_tags: [ scale.id ],
        }
      }
    }

    const match = CategoryHelpers._optionMatchForCategorySpecificsSize(size, categorySpecificsData, 'size')
    if (match) {
      const scaleKey = CategoryHelpers._scaleKeyForCategorySpecificsSize(size, categorySpecificsData, 'size')
      return {
        size: {
          size_obj: match,
          size_set_tags: [ scaleKey ],
        }
      }
    }

    return {}
  },

  // A normalized format for the size
  // Take the first merchant input category specifics we see and translate them to this format
  // { 
  //   display: string, (full display name)
  //   search: string, (search term.. i.e. a number?)
  //   scale: string, (scale name, or null if "standard / regular / no_scale")
  //  }
  normalizedSizeForItem: (item) => {
    if (!item || !item.merchants) {
      return null
    }

    for (let [ merchantName, merchantValues ] of Object.entries(item.merchants)) {

      if (
        !merchantValues ||
        !merchantValues.marketplaceSpecifics ||
        !merchantValues.marketplaceSpecifics.categorySpecifics
      ) {
        continue
      }
      const specifics = merchantValues.marketplaceSpecifics.categorySpecifics

      switch (merchantName) {
        case 'poshmark': {
          if (
            !specifics.size ||
            !specifics.size.size_obj ||
            !specifics.size.size_set_tags ||
            !specifics.size.size_set_tags.length
          ) {
            continue
          }
          const sizeSet = specifics.size.size_set_tags[0]
          const { short, display } = specifics.size.size_obj

          // Handle shoes
          const match = /US ([\d\.]+) \(Euro ([\d\.]+)\)/.exec(display)
          let normalizedSize = {
            display,
            search: short || display,
            scale: sizeSet === 'standard' ? null : sizeSet
          }
          if (match && match.length > 2) {
            normalizedSize.shoe_size_us = +match[1]
            normalizedSize.shoe_size_euro = +match[2]
          }
          return normalizedSize
        }
        case 'ebay': {
          const size = specifics['Size'] || {}
          const sizeType = specifics['Size Type'] || {}
          // Normalize to Poshmark scales
          const normalizedScale = (sizeType) => {
            switch (sizeType.id) {
              case 'Regular':
                return null
              case 'Plus':
                return 'plus'
              case 'Petites':
                return 'petite'
              case 'Tall':
                return null
              case 'Juniors':
                return 'juniors' // TODO consider handling boys/girls/baby
              case 'Maternity':
                return 'maternity'
              default:
                return null
            }
          }
          return {
            display: size.display,
            search: size.id,
            scale: normalizedScale(sizeType)
          }
        }
        // TODO need to handle the rest of the merchants
        default:
          continue
      }
    }

    return null
  }
}

export default CategoryHelpers